"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GCPTermsAndConditions = void 0;
const DomElement_1 = require("./DomElement");
const defaults_1 = require("./defaults");
const templates_1 = require("./templates");
class GCPTermsAndConditions extends DomElement_1.DomElement {
    constructor(options, selector = null) {
        selector = selector || GCPTermsAndConditions.createElement();
        super(selector, options);
    }
    load(type) {
        let url = `${this.getOptions('url')}/terms/verify`;
        if (this.getOptions('force')) {
            url += `?force=true`;
        }
        this._httpGet(url)
            .then((r) => {
            if (r.json && r.json.length > 0) {
                const matched = r.json.find((t) => t.terms_type_code === type);
                if (matched) {
                    this._terms = matched;
                    this.show();
                }
                else {
                    this._trigger(defaults_1.EVENT_LOADED, { code: null });
                }
            }
            else {
                this._trigger(defaults_1.EVENT_LOADED, { code: null });
            }
        }).catch((error) => {
            this._logError(error);
        });
    }
    show() {
        this.fetchTermsContent(this._terms.terms_type_code, this._terms.latest_id)
            .then((termsContent) => {
            this.setOptions('content', termsContent);
            this._render();
        })
            .catch((error) => {
            this._logError(error);
        });
    }
    downloadTermsByType(type, pdf = false, solution_code = null) {
        const baseUrl = `${this.getOptions('url')}/terms/solutions`;
        const termsUrl = solution_code ? `${baseUrl}/${solution_code}` : baseUrl;
        return this._httpGet(termsUrl)
            .then((response) => {
            const terms = response.json || [];
            const matchedTerms = terms.find((term) => term.terms_type_code === type);
            if (matchedTerms) {
                return this.fetchTermsContent(matchedTerms.terms_type_code, matchedTerms.latest_id, pdf);
            }
            else {
                throw new Error(`Terms with type ${type} not found`);
            }
        })
            .then((termsContent) => {
            this._trigger(defaults_1.EVENT_LOADED, { terms: termsContent });
            return;
        })
            .catch((error) => {
            this._logError(error);
            this._trigger(defaults_1.EVENT_ERROR, { message: error.message });
            return null;
        });
    }
    fetchTermsContent(termsTypeCode, termsId, pdf = false) {
        const termsContentUrl = `${this.getOptions('url')}/terms/types/${termsTypeCode}/${termsId}?pdf=${pdf}`;
        return this._httpGet(termsContentUrl)
            .then((response) => {
            if (pdf) {
                return response.blob;
            }
            else {
                return response.json.content;
            }
        })
            .catch(error => {
            throw new Error(`Error fetching terms content: ${error.message}`);
        });
    }
    accept(e) {
        this._handleAccept(e);
    }
    /**
     * Instantiate the base element, which will hold the form.
     * @return {HTMLDivElement}
     */
    static createElement() {
        const exists = document.getElementsByClassName('gcpt-modal');
        if (exists && exists.length > 0) {
            return exists[0];
        }
        const el = document.createElement('div');
        el.className = 'gcpt-modal';
        return el;
    }
    static createToast() {
        const el = document.createElement('div');
        el.className = 'gcpt-toast';
        el.setAttribute('id', 'gcpt-toast' + Date.now());
        return el;
    }
    _logError(er) {
        console.error('GCP Terms Error: ', er);
    }
    /**
     * Create the form and bind its events.
     * @return void
     * @private
     */
    _render() {
        if (this.getOptions('ui') === defaults_1.UI_CUSTOM) {
            this.getElement().innerHTML = (0, templates_1.custom)(this._options);
        }
        else {
            this.getElement().innerHTML = (0, templates_1.dialog)(this._options);
            document.body.appendChild(this.getElement());
            if (this.hasElement('#gcpt-accept')) {
                this.on('click', this._handleAccept, this.getElement('#gcpt-accept'));
            }
            if (this.hasElement('#gcpt-decline')) {
                this.on('click', this._handleReject, this.getElement('#gcpt-decline'));
            }
        }
        this._trigger(defaults_1.EVENT_LOADED, { code: this._terms.terms_type_code });
    }
    /**
     * Close the form modal.
     * @param {MouseEvent} e
     * @private
     */
    _handleReject(e) {
        e.preventDefault();
        this._trigger(defaults_1.EVENT_REJECTED, { code: this._terms.terms_type_code });
    }
    /**
     * Validate the data and display messages accordingly.
     * @param {MouseEvent} e
     * @private
     */
    _handleAccept(e) {
        e.preventDefault();
        this._setLoading(true)
            ._clearMessage();
        const url = `${this.getOptions('url')}/terms/types/${this._terms.terms_type_code}/${this._terms.latest_id}`;
        this._httpPost(url, {})
            .then((r) => {
            var _a;
            if (r && ((_a = r === null || r === void 0 ? void 0 : r.response) === null || _a === void 0 ? void 0 : _a.ok)) {
                this._trigger(defaults_1.EVENT_ACCEPTED, { code: this._terms.terms_type_code, id: this._terms.latest_id, version: this._terms.latest_version });
                this._clean();
            }
            else {
                this._trigger(defaults_1.EVENT_ERROR, r.json);
            }
            return this._message(r.json, r.response.ok, r.response);
        })
            .catch((error) => {
            this._message(error, false, error)
                ._setLoading(false);
        });
    }
    _clean() {
        if (this.getOptions('ui') === defaults_1.UI_DEFAULT) {
            this.remove();
        }
        this._terms = null;
    }
    _headers() {
        const headers = this.getOptions('headers');
        if (headers && headers instanceof Function) {
            return headers().then((data) => {
                return Object.assign(defaults_1.HEADERS, data);
            });
        }
        return new Promise(() => {
            return defaults_1.HEADERS;
        });
    }
    /**
     * Send the data to the server.
     * @param {string} url
     * @param {string} method
     * @param {object} data
     * @return {Promise<Response>}
     * @private
     */
    _httpRequest(url, method = 'GET', data = null) {
        let response;
        const options = { method };
        if (data) {
            options.body = JSON.stringify(data);
        }
        return this._headers()
            .then((headers) => {
            options.headers = headers;
            return window.fetch(url, options).then((r) => {
                var _a;
                response = r;
                if ((_a = r.headers.get('Content-Type')) === null || _a === void 0 ? void 0 : _a.includes('application/pdf')) {
                    return r.blob().then(blob => {
                        return { blob, response };
                    });
                }
                else {
                    return r.json().then(json => {
                        return { json, response };
                    });
                }
            });
        });
    }
    _httpPost(url, data) {
        return this._httpRequest(url, 'POST', data);
    }
    _httpGet(url) {
        return this._httpRequest(url, 'GET');
    }
    /**
     * Show the user the request is being made.
     * @param {boolean} state
     * @return {GCPTermsAndConditions}
     * @private
     */
    _setLoading(state) {
        const btn = this.getElement('#gcpt-accept');
        if (!btn) {
            return this;
        }
        if (state) {
            btn.innerHTML = (0, templates_1.spinner)();
        }
        else {
            btn.innerHTML = defaults_1.BTN_ACCEPT;
        }
        return this;
    }
    message(message) {
        this._message({ message, force: true }, false, {});
    }
    /**
     * Show an error or success message in the form.
     * @param {object} body
     * @param {boolean} success
     * @param {Response} response
     * @return {GCPTermsAndConditions}
     * @private
     */
    _message(body, success, response) {
        if (this.getOptions('ui') === defaults_1.UI_CUSTOM && !body.force) {
            return this;
        }
        const el = document.body.appendChild(GCPTermsAndConditions.createToast());
        let msg = body.message;
        if (typeof msg !== 'string') {
            msg = response.statusText;
        }
        el.textContent = msg;
        if (success) {
            el.className += ' show success';
        }
        else {
            el.className += ' show error';
        }
        setTimeout(function () {
            el.className = el.className.replace('show', '');
            setTimeout(() => {
                el.remove();
            }, 200);
        }, 3000);
        return this;
    }
    /**
     * Hide the message element.
     * @return {GCPTermsAndConditions}
     * @private
     */
    _clearMessage() {
        // this.getElement('#gcpt-msg').className = '';
        return this;
    }
}
exports.GCPTermsAndConditions = GCPTermsAndConditions;
